import { isCaregiverVerified } from "../../utils/mapMarketplaceCaregiver";
import ProfileAvatar from "../shared/ProfileAvatar";
import "./PreferredCaregiversPanel.css";

export const MAX_CAREGIVER_PREFERENCES = 3;

function formatRatingLine(rating, reviewCount) {
  const r = Number(rating);
  if (!Number.isFinite(r) || r <= 0) {
    return reviewCount > 0 ? `New (${reviewCount} reviews)` : "New";
  }
  const reviews =
    reviewCount > 0 ? ` (${reviewCount} review${reviewCount === 1 ? "" : "s"})` : "";
  return `${r.toFixed(1)}${reviews}`;
}

function formatExperienceLine(years) {
  const y = Number(years);
  if (!Number.isFinite(y) || y <= 0) return "Experience on request";
  return `${y}+ Years Experience`;
}

function PersonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#2563eb" />
      <path d="M12 10v6M12 7h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
    </svg>
  );
}

export default function PreferredCaregiversPanel({
  preferences,
  onRemove,
  onBook,
  onHire,
  onMove,
  browseBlocked = false,
  bookLabel = "Call Me",
  hireLabel = "Hire Now",
}) {
  const slots = Array.from({ length: MAX_CAREGIVER_PREFERENCES }, (_, index) => preferences[index] || null);
  const filled = preferences.length > 0;

  return (
    <div className="caregiver-prefs">
      <header className="caregiver-prefs__head">
        <h2>Your Shortlist</h2>
        <p>Shortlist up to 3 caregivers, then hire the one you trust most.</p>
      </header>

      <ul className="caregiver-prefs__list">
        {slots.map((caregiver, index) => (
          <li key={`pref-slot-${index}`} className="caregiver-prefs__slot">
            {caregiver ? (
              <article className="pref-card">
                <div className="pref-card__header">
                  <span className="pref-card__label">Preference {index + 1}</span>
                  <button
                    type="button"
                    className="pref-card__remove"
                    onClick={() => onRemove(caregiver.id)}
                    aria-label={`Remove ${caregiver.name}`}
                  >
                    ×
                  </button>
                </div>
                <div className="pref-card__body">
                  <ProfileAvatar
                    name={caregiver.name}
                    imageUrl={caregiver.profileImageUrl}
                    storageKey={caregiver.profileImageStorageKey}
                    className="pref-card__photo"
                    initialsFallback="C"
                  />
                  <div className="pref-card__details">
                    <div className="pref-card__name-row">
                      <strong>{caregiver.name}</strong>
                      {isCaregiverVerified(caregiver) ? (
                        <span className="pref-card__verified" title="Verified caregiver">
                          <span aria-hidden="true">✓</span>
                        </span>
                      ) : null}
                    </div>
                    <p className="pref-card__rating">
                      <StarIcon />
                      {formatRatingLine(caregiver.rating, caregiver.reviewCount)}
                    </p>
                    <p className="pref-card__experience">
                      <PersonIcon />
                      {formatExperienceLine(caregiver.years)}
                    </p>
                    <button
                      type="button"
                      className="pref-card__call"
                      disabled={caregiver.isAvailable === false || browseBlocked}
                      onClick={() => onBook(caregiver)}
                      aria-label={`${bookLabel} ${caregiver.name}`}
                    >
                      {bookLabel}
                    </button>
                    {onHire ? (
                      <button
                        type="button"
                        className="pref-card__hire"
                        disabled={caregiver.isAvailable === false || browseBlocked}
                        onClick={() => onHire(caregiver)}
                        aria-label={`${hireLabel} ${caregiver.name}`}
                      >
                        {hireLabel}
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            ) : (
              <div className="pref-card pref-card--empty">
                <span className="pref-card__label">Preference {index + 1}</span>
                <p>Add a caregiver from the list to compare and hire directly.</p>
              </div>
            )}
          </li>
        ))}
      </ul>

      {filled ? (
        <aside className="caregiver-prefs__how">
          <div className="caregiver-prefs__how-head">
            <InfoIcon />
            <strong>How it works</strong>
          </div>
          <p>We will first try to assign Preference 1 caregiver based on availability.</p>
          <p>If that Care Attendant declines due to shift overlap, select Preference 2, then Preference 3.</p>
          <p>You will be notified once a caregiver is confirmed.</p>
        </aside>
      ) : null}
    </div>
  );
}
